"use client"

/* ds-typo-ignore-file — EN-only prototype ad LP: intentional sub-DS sizes in the immersive scroll hero scrim/KPI labels (not shipped DS surfaces) */

import Link from "next/link"
import Image from "next/image"
import { useState } from "react"
import { useTranslations } from "next-intl"
import { goKeys as k } from "@/i18n/go-keys"
import { useLocalePath } from "@/lib/use-localized-navigation"
import { blobMediaUrl } from "@/lib/blob-url"
import { BiomarkerDiseaseExplorer } from "@/components/ui/biomarker-disease-explorer"
import { baseBloodTest } from "@/lib/base-blood-test"
import { useCountry } from "@/components/layout/country-provider"
import { GoogleRating } from "@/components/ui/google-rating"
import { Button, ArrowRightIcon } from "@/components/ui/button"
import { HeroTrustStrip } from "@/components/ui/hero-trust-strip"
import { PartnerLogoMarquee } from "@/components/ui/partner-logo-marquee"
import { FaqAccordion } from "@/components/ui/faq-accordion"
import { homeKeys as hk } from "@/i18n/home-keys"
import type { ReviewerExpert } from "@/lib/reviewer-experts"
import { DoctorsSection } from "../../../_components/doctors-section"
import { TestimonialsSection } from "../../../_components/testimonials-section"
import type { Testimonial } from "@/lib/cms"
import { SIGNS, FAQ_KEYS } from "./heart-health-data"
import { SignGlyph } from "./heart-health-sign-glyphs"
import {
  SECTION,
  BookingLink,
  CtaLabel,
  useBookingHref,
} from "./heart-health-components"
import {
  ImmersiveFullPicture,
  HeartHealthMriImmersive,
} from "./heart-health-immersive"
import {
  HeartHealthMeasure,
  HeartHealthProcess,
  HeartHealthReport,
  HeartHealthGpCompare,
  HeartHealthPricing,
  HeartHealthClosingCta,
} from "./heart-health-sections"

export default function HeartHealthClient({
  bloodDrawCount,
  experts,
  testimonials,
  showImaging,
}: {
  /** Live count of active blood-draw labs (CMS `locations` where type === "blood"). */
  bloodDrawCount: number
  /** Hand-picked diagnostics/blood advisors (CMS reviewers), in display order. */
  experts: ReviewerExpert[]
  /** CMS testimonials for the reviews carousel (empty → section self-hides). */
  testimonials: Testimonial[]
  /**
   * Is this LP's imaging arm actually sellable in the visitor's market?
   * Resolved once on the server (page.tsx) from the CMS products control plane
   * + the country's booking lifecycle. False → every imaging surface is hidden
   * rather than downgraded to a coming-soon/waitlist block: an ad LP that
   * advertises a product the market can't buy is a conversion leak, not a
   * teaser (AHD-8843, Michèle's NL review).
   */
  showImaging: boolean
}) {
  const t = useTranslations("go")
  const ht = useTranslations("home")
  const lp = useLocalePath()
  const { code: country } = useCountry()
  // Active cross-marker insight reflected in the readout panel (section 4).
  const [activeInsight, setActiveInsight] = useState(0)

  const bookingHref = useBookingHref()

  return (
    <>
      {/* ── 1. Hero — full-bleed video with overlaid copy (style ported 1:1 from /go/immunity) ── */}
      {/* NOTE: hero imagery is a prototype placeholder (hiker / everyday energy) — swap for final heart-specific art before launch. */}
      <section className="relative isolate flex min-h-[calc(80vh+40px)] items-center overflow-hidden bg-neutral-900 text-white">
        {/* full-bleed cover image: subject is weighted right, the misty water on
            the left keeps the headline area clear. On mobile center-crop pushes
            the subject out of frame, so bias the crop rightward on small screens. */}
        <Image
          src={blobMediaUrl("/images/metabolism/hero-metabolism-hike.webp")}
          alt=""
          fill
          priority
          sizes="100vw"
          className="object-cover object-[68%_center] md:object-center"
          aria-hidden
        />
        {/* top scrim for the transparent navbar */}
        <div
          aria-hidden
          className="absolute inset-0 bg-gradient-to-b from-black/55 via-transparent to-transparent"
        />
        {/* legibility scrim: bottom-up on mobile, left-to-right on desktop */}
        <div
          aria-hidden
          className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/35 to-black/10 md:bg-gradient-to-r md:from-black/72 md:via-black/22 md:to-transparent"
        />
        <div className={`${SECTION} relative z-10 w-full`}>
          <div className="flex max-w-[636px] flex-col items-start gap-6 py-28">
            <p className="type-label-md text-white/60 uppercase">
              {t(k.heartHealthHeroEyebrow)}
            </p>
            <h1 className="type-display-md md:type-display-lg leading-[0.98] text-white">
              {t(k.heartHealthHeroHeading)}
            </h1>
            <p className="type-body-lg max-w-[480px] text-white/85">
              {t(k.heartHealthHeroBody)}
            </p>
            <GoogleRating
              variant="onDark"
              staticOnDark
              rating=""
              reviewLabel={t(k.heartHealthHeroReviewLabel)}
              className="self-start"
            />
            <BookingLink
              href={bookingHref}
              location="heart-health-hero"
              className="block w-full sm:inline-block sm:w-auto"
            >
              <Button
                size="lg"
                variant="primary"
                className="group w-full sm:w-auto"
                iconRight={
                  <ArrowRightIcon className="transition-transform duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:translate-x-1" />
                }
              >
                <CtaLabel label={t(k.heartHealthHeroCta1)} />
              </Button>
            </BookingLink>
          </div>
        </div>
        {/* trust strip — same component as the membership hero, left-aligned */}
        <HeroTrustStrip
          align="left"
          compactMobile
          className="pb-4 md:pb-5"
          items={[
            {
              value: t(k.heartHealthHeroKpi1Value),
              label: t(k.heartHealthHeroKpi1Label),
            },
            {
              value: t(k.heartHealthHeroKpi2Value),
              label: t(k.heartHealthHeroKpi2Label),
            },
            {
              value: t(k.heartHealthHeroKpi3Value),
              label: t(k.heartHealthHeroKpi3Label),
            },
          ]}
        />
      </section>

      {/* ── 2. Partner logos — a fixed, curated row (no marquee) ── */}
      <div className="mx-auto max-w-[1100px] px-6 pt-4 pb-6 md:px-10 md:pt-8">
        <PartnerLogoMarquee
          layout="static"
          only={[
            "Unilabs",
            "Oxford",
            "Google",
            "Siemens Healthineers",
            "TUM",
            "tech.eu",
          ]}
        />
      </div>

      {/* ── 3. Signs — symptom cards (design ported 1:1 from /go/immunity) ── */}
      <section className="py-28 md:py-40">
        <div className={`${SECTION} flex flex-col items-center gap-12`}>
          {/* header — eyebrow + heading */}
          <div className="flex flex-col items-center gap-4 text-center">
            <p className="type-label-md text-neutral-600 uppercase">
              {t(k.heartHealthSignsEyebrow)}
            </p>
            <h2 className="type-display-md max-w-[600px] text-neutral-900">
              {t(k.heartHealthSignsHeading)}
            </h2>
            <p className="type-body-xl max-w-[620px] text-neutral-500">
              {t(k.heartHealthSignsBody)}
            </p>
          </div>

          {/* cards */}
          <div className="grid w-full grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {SIGNS.map((s) => (
              <div
                key={s.titleKey}
                className="flex flex-col items-center gap-4 rounded-[24px] bg-white p-10 text-center"
              >
                <span className="flex items-center justify-center text-neutral-900">
                  <s.Icon className="h-12 w-12" />
                </span>
                {/* reserve 2 lines (top-aligned) so single-line titles keep a
                    consistent icon-to-title gap and the description still starts
                    on the same baseline across every card */}
                <h3 className="type-title-lg flex min-h-[52px] max-w-[200px] items-start justify-center text-neutral-900">
                  {t(s.titleKey)}
                </h3>
                <p className="type-body-xl text-neutral-500">{t(s.descKey)}</p>
              </div>
            ))}
          </div>

          {/* closing — "stop guessing, start knowing" wedge, merged into the signs close: icon kept, big heading, no eyebrow */}
          <div className="mt-16 mb-12 flex flex-col items-center gap-5 text-center md:mt-28 md:mb-24">
            <span className="text-neutral-900">
              <SignGlyph name="brain" />
            </span>
            <p className="type-display-md max-w-[760px] text-neutral-900">
              {t(k.heartHealthWedgeHeading1)}
              <span className="text-bright-blue-600 block">
                {t(k.heartHealthWedgeHeading2)}
              </span>
            </p>
            <p className="type-title-lg max-w-[820px] font-medium text-neutral-600">
              {t(k.heartHealthWedgeBody)}
            </p>
          </div>
        </div>
      </section>

      {/* ── 4. The full picture — immersive scroll-blur ── */}
      <section>
        <div className={SECTION}>
          <ImmersiveFullPicture
            activeIndex={activeInsight}
            onPick={setActiveInsight}
          />
        </div>
      </section>

      {/* ── 5. What we measure — four marker groups + the SEO cross-links.
          The calcium-score link inside is imaging-gated. ── */}
      <HeartHealthMeasure showImaging={showImaging} />

      {/* ── 7b. Where the risk is stored — the "see the cause" MRI beat,
          immersive pinned-scroll variant (text rides in, then the imaging cards).
          Imaging-only markets; hidden entirely where the scans aren't sellable. ── */}
      {showImaging && <HeartHealthMriImmersive />}

      {/* ── 7c. Bridge: one draw, your whole body (coverage explorer). Sits after
          the MRI beat so the imaging arm comes before the deep blood dive. ── */}
      <section className="bg-neutral-50 py-16 md:py-24">
        <div className={SECTION}>
          {/* Centered header — matches the Advanced Blood Test explorer header */}
          <div className="mx-auto flex max-w-[720px] flex-col items-center gap-3 text-center md:gap-4">
            <p className="type-label-md text-neutral-600 uppercase">
              {t(k.heartHealthCoverageEyebrow)}
            </p>
            <h2 className="type-display-md text-neutral-900">
              {t(k.heartHealthCoverageHeading)}
            </h2>
            <p className="type-body-lg max-w-[520px] text-neutral-600">
              {t(k.heartHealthCoverageBody)}
            </p>
          </div>
          <div className="mt-12">
            {/* Live CMS-backed explorer (locale-aware) — biomarker-only, same
                component the real /biomarkers page + sibling /go LPs use. The
                former static snapshot rendered English-only regardless of
                locale (AHD-8117). `hideHeader` suppresses its own duplicate
                header + internal padding so it aligns with this section. */}
            <BiomarkerDiseaseExplorer
              packages={[baseBloodTest(country).biomarkerPackage]}
              tabsAllowed={["biomarkers"]}
              defaultCategorySlug="cardiovascular"
              hideHeader
            />
          </div>
          <div className="mt-10 flex flex-col items-center gap-3">
            <BookingLink
              href={bookingHref}
              location="heart-health-coverage"
              className="block w-full sm:inline-block sm:w-auto"
            >
              <Button
                size="lg"
                variant="primary"
                className="group w-full sm:w-auto"
                iconRight={
                  <ArrowRightIcon className="transition-transform duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:translate-x-1" />
                }
              >
                <CtaLabel label={t(k.heartHealthCoverageCta)} />
              </Button>
            </BookingLink>
          </div>
        </div>
      </section>

      {/* ── 8. How it works (scroll-linked stepper) ── */}
      <HeartHealthProcess
        bloodDrawCount={bloodDrawCount}
        showImaging={showImaging}
      />

      {/* ── 7d. Your report ── */}
      <HeartHealthReport />

      {/* ── 9b. Testimonials carousel (CMS-backed; self-hides if none) — social
          proof right before the offer. ── */}
      {/* Shorter video tiles (4/5 vs the default 9/16) crop the rail ~30% — the
          text cards self-stretch to match, so the whole section shrinks. */}
      <TestimonialsSection
        cmsTestimonials={testimonials}
        videoAspectClass="aspect-[4/5]"
      />

      {/* ── 9c. Why not just my GP? — compact ordinary-vs-Ahead comparison
          table (reuses the "How Ahead compares" design). ── */}
      <HeartHealthGpCompare showImaging={showImaging} />

      {/* ── 10. The offer — blood vs blood+imaging; blood vs membership where
          the imaging arm isn't sellable; blood alone where neither is ── */}
      <HeartHealthPricing showImaging={showImaging} />

      {/* ── 11. Experts — shared DoctorsSection used exactly like the homepage
          (columnHeading + photo grid), minus the MRI-framed count stat. Experts
          are the diagnostics/blood-leaning advisors hand-picked in page.tsx. ── */}
      <DoctorsSection
        initialExperts={experts}
        showCount={false}
        columnHeading={
          <h2 className="type-display-md max-w-md text-neutral-900">
            {ht(hk.expertsHeading)}
          </h2>
        }
      />

      {/* ── 12. FAQ — same set + layout as /go/bloodtest (home namespace) ── */}
      <section className="py-16 md:py-24">
        <div className="mx-auto grid max-w-[1440px] grid-cols-1 gap-8 px-10 md:grid-cols-[1fr_2fr] md:gap-12">
          <div>
            <h2 className="type-display-md text-neutral-900">
              {ht(hk.faqHeading)}
            </h2>
            <Link
              href={lp("/faq")}
              className="border-aubergine-800/30 text-aubergine-800 hover:bg-aubergine-800 hover:border-aubergine-800 type-title-sm mt-4 inline-flex items-center justify-center gap-1.5 rounded-full border px-5 py-2.5 transition-colors hover:text-white"
            >
              {ht(hk.faqLink)} &rarr;
            </Link>
          </div>
          <div>
            <FaqAccordion
              items={FAQ_KEYS.map((faq) => ({
                question: ht(faq.q),
                answer: ht(faq.a),
              }))}
            />
          </div>
        </div>
      </section>

      {/* ── 13. Closing CTA ── */}
      <HeartHealthClosingCta />
    </>
  )
}
